import { omit } from 'lodash';
import type React from 'react';
import { useMemo } from 'react';

import {
  COMPOSITION_GET_DATA_V3,
  type CompositionV3DataQuery,
  PAY_V2_QUERY,
} from '@revelio/data-access';

import { useCustomSkillTaxonomyTree } from '../../../../../shared/selection-lists/hooks/useCustomSkillTaxonomyTree';
import { useFilterContext } from '../../../filters/contexts/FilterProvider';
import { useEffectiveFilters } from '../../../filters/hooks/useEffectiveFilters';
import {
  FilterType,
  type PrimaryFilterId,
} from '../../../filters/types/filters';
import {
  type CompositionV3BreakdownDimension,
  getCompositionV3BreakdownOptions,
} from '../../../filters/utils/composition-v3/getCompositionV3BreakdownOptions';
import {
  getCompositionV3Variables,
  getCustomSkillDeepestLevelFromFilters,
  normalizeCompositionEffectiveFilters,
} from '../../../filters/utils/composition-v3/getCompositionV3Variables';
import { mergeDrilldownIntoCompositionV3Filters } from '../../../filters/utils/composition-v3/merge-drilldown-into-composition-v3-filters';
import { getMonthRefsFromCompositionV3Filters } from '../../../filters/utils/composition-v3/month-refs-from-v3-filters';
import {
  deriveNestedTreemapView,
  getBreakdownLevelField,
} from '../../../filters/utils/composition-v3/treemapNestedLevels';
import { getV2Filters } from '../../../filters/utils/getV2Filters';
import { stripEntitiesFromFilters } from '../../../filters/utils/role-skill-ent-kind';
import type { TreemapDrilldownSegment } from '../../../filters/utils/treemapDrilldownLevels';
import {
  type NestedTreemapParentTotal,
  nestedTreemapLookupKey,
} from '../../Chart/utils/buildNestedTreemapData';
import { TOP_N_LIMIT } from '../constants';
import {
  getTopCompaniesFromPayV2,
  payV2EntitiesFromSelections,
} from '../TopNDataProvider';
import { DataProviderQueryStatus } from '../types';
import {
  combineQueryStatus,
  useUrqlQueryWithStatus,
} from '../utils/data-provider-results-utils';
import { isCompositionV3FullMetrics } from './composition-v3/composition-v3-graphql-response';
import {
  isEmptyCompositionBucket,
  isOtherCompositionBucket,
  OTHER_BUCKET_LABEL,
} from './composition-v3/is-empty-composition-bucket';
import {
  type NestedTreemapParent,
  useNestedTreemapParentLookup,
} from './composition-v3/useNestedTreemapParentLookup';

type CompositionV3Payload = NonNullable<
  CompositionV3DataQuery['composition_v3']
>;

export type DrilldownFilter =
  | {
      segment: 'geography';
      level: 'region' | 'country' | 'state' | 'msa' | 'city';
      id: number;
    }
  | {
      segment: 'role';
      levelId: 'role_k7' | 'role_k150' | 'role_k1500' | 'role_k15000';
      id: string;
    }
  | {
      segment: 'rics';
      level: 'rics_k10' | 'rics_k50' | 'rics_k400' | 'company';
      id: number;
    }
  | {
      segment: 'skills';
      level: 'skill_0' | 'skill_1' | 'skill_2' | 'skill_3' | 'skill_4';
      id: string;
    };

const SEGMENT_TO_V3_BREAKDOWN_DIMENSION: Record<
  DrilldownFilter['segment'],
  CompositionV3BreakdownDimension
> = {
  geography: 'geography',
  role: 'role',
  rics: 'rics',
  skills: 'skill',
};

function filterOutSkillsOtherBucket(
  data: CompositionV3Payload | undefined,
  segment: TreemapDrilldownSegment
): CompositionV3Payload | undefined {
  if (data === undefined || segment !== 'skills') return data;
  return {
    ...data,
    entities: data.entities.map((entity) => ({
      ...entity,
      metrics: {
        ...entity.metrics,
        skills: entity.metrics.skills.filter(
          (skill) => !isOtherCompositionBucket(skill.id)
        ),
      },
    })),
  };
}

type Props = {
  primaryDimension: PrimaryFilterId;
  segment: TreemapDrilldownSegment;
  /** Active drilldown, or `null` for the initial (top-level) treemap. */
  drilldown: DrilldownFilter | null;
  children: (result: {
    data: CompositionV3Payload | undefined;
    monthRefs: string[];
    status: DataProviderQueryStatus;
    /** Child→parent grouping lookup; `null` when the view renders flat. */
    parentLookup: Map<string, NestedTreemapParent> | null;
    parentTotals: Map<string, NestedTreemapParentTotal>;
    isNested: boolean;
    /** Level id pushed onto the drilldown stack on sub-rectangle click. */
    displayLevelId: string;
    isDisplayLeaf: boolean;
    /** Level id pushed on group-header click; `null` when not nested. */
    parentLevelId: string | null;
  }) => React.ReactNode;
};

export function NestedCompositionTreemapDrilldownProvider({
  primaryDimension,
  segment,
  drilldown,
  children,
}: Props) {
  const effectiveFilters = useEffectiveFilters({ viewGranularity: 'month' });
  const { customSkillTree, isCustomSkillTaxonomyPending } =
    useCustomSkillTaxonomyTree();
  const { activeFilterMap, geographyMode } = useFilterContext();

  const baseVariablesV21 = getCompositionV3Variables({
    primaryDimension,
    effectiveFilters,
    isSnapshot: true,
    getSelectionBreakdowns: true,
    geographyMode,
    customSkillTree,
    activeFilterMap,
  });

  const mergedFiltersV21 =
    drilldown === null
      ? baseVariablesV21.filters
      : mergeDrilldownIntoCompositionV3Filters(
          baseVariablesV21.filters,
          drilldown
        );

  const normalizedEffectiveForBreakdown =
    normalizeCompositionEffectiveFilters(effectiveFilters);

  const breakdownCustomSkillDeepestLevel =
    drilldown?.segment === 'skills'
      ? drilldown.level
      : getCustomSkillDeepestLevelFromFilters(normalizedEffectiveForBreakdown);

  const isRicsCompanyBreakdown =
    drilldown?.segment === 'rics' &&
    (drilldown.level === 'rics_k400' || drilldown.level === 'company');

  const naturalBreakdownOptions = getCompositionV3BreakdownOptions({
    compositionFilters: mergedFiltersV21,
    customSkillTree,
    customSkillDeepestLevel: breakdownCustomSkillDeepestLevel,
    geographyMode,
  });
  const naturalLevel = naturalBreakdownOptions[getBreakdownLevelField(segment)];

  const {
    childDepth,
    breakdownLevelOverrides,
    parentEntKind,
    parentBreakdownLevelOverrides,
    displayLevelId,
    isDisplayLeaf,
    parentLevelId,
  } = deriveNestedTreemapView({
    segment,
    naturalLevel,
    geographyMode,
    isRicsCompanyBreakdown,
  });

  const breakdownOptionsV21 = getCompositionV3BreakdownOptions({
    compositionFilters: mergedFiltersV21,
    customSkillTree,
    customSkillDeepestLevel: breakdownCustomSkillDeepestLevel,
    includedBreakdownDimensions: [SEGMENT_TO_V3_BREAKDOWN_DIMENSION[segment]],
    geographyMode,
    breakdownLevelOverrides,
  });

  const { parentLookup, fetching: parentLookupFetching } =
    useNestedTreemapParentLookup({
      segment,
      childDepth,
      geographyMode,
      customSkillTree,
      customSkillTreeFetching: isCustomSkillTaxonomyPending,
    });

  const isNested = parentLookup !== null && childDepth !== null;

  const parentBreakdownOptionsV21 = getCompositionV3BreakdownOptions({
    compositionFilters: mergedFiltersV21,
    customSkillTree,
    customSkillDeepestLevel: breakdownCustomSkillDeepestLevel,
    includedBreakdownDimensions: [SEGMENT_TO_V3_BREAKDOWN_DIMENSION[segment]],
    geographyMode,
    breakdownLevelOverrides: parentBreakdownLevelOverrides,
  });

  const [{ data: parentDataV21 }] =
    useUrqlQueryWithStatus<CompositionV3DataQuery>({
      query: COMPOSITION_GET_DATA_V3,
      variables: {
        ...baseVariablesV21,
        filters: mergedFiltersV21,
        breakdown_options: parentBreakdownOptionsV21,
      },
      pause: parentLevelId === null || parentEntKind === null || !isNested,
    });

  const parentTotals = useMemo(() => {
    const totals = new Map<string, NestedTreemapParentTotal>();
    const entities = parentDataV21?.composition_v3?.entities ?? [];
    for (const entity of entities) {
      const metrics = isCompositionV3FullMetrics(entity.metrics)
        ? entity.metrics
        : undefined;
      const buckets = metrics?.[segment] ?? [];
      for (const bucket of buckets) {
        if (isEmptyCompositionBucket(bucket?.id)) {
          continue;
        }
        const lastPoint = bucket.headcount[bucket.headcount.length - 1];
        if (!lastPoint) continue;
        const key = nestedTreemapLookupKey(bucket.id);
        const existing = totals.get(key);
        const share =
          typeof lastPoint.share === 'number' ? lastPoint.share : null;
        const mergedShare = (() => {
          if (existing === undefined) return share;
          if (existing.share === null || share === null) return null;
          return existing.share + share;
        })();
        const rawName = bucket.short_name ?? '';
        const name = isOtherCompositionBucket(bucket.id)
          ? OTHER_BUCKET_LABEL
          : `${rawName.charAt(0).toUpperCase()}${rawName.slice(1)}`;
        totals.set(key, {
          name: existing?.name ?? name,
          value: (existing?.value ?? 0) + lastPoint.count,
          share: mergedShare,
        });
      }
    }
    return totals;
  }, [parentDataV21, segment]);

  const drilldownVariablesV21 = {
    ...baseVariablesV21,
    filters: mergedFiltersV21,
    breakdown_options: breakdownOptionsV21,
  };

  const primaryFilter = effectiveFilters.find((f) => f.isPrimary);
  const primaryEntities =
    primaryFilter?.type === FilterType.TREE
      ? payV2EntitiesFromSelections(primaryFilter.selections)
      : [];

  const topNFilters =
    isRicsCompanyBreakdown && drilldown !== null && drilldown.segment === 'rics'
      ? {
          ...getV2Filters({ primaryDimension, effectiveFilters }),
          rics_k400: [drilldown.id],
        }
      : null;

  const [{ data: topNData, status: topNStatus }] = useUrqlQueryWithStatus({
    query: PAY_V2_QUERY,
    variables: {
      req: {
        entities: primaryEntities,
        filters: topNFilters ?? { raw: false },
        ...(geographyMode === 'administrative' && {
          options: { geo_breakdown_state_over_msa: true },
        }),
      },
    },
    pause: !isRicsCompanyBreakdown || primaryEntities.length === 0,
  });

  const topEntities =
    isRicsCompanyBreakdown && topNData?.pay_v2
      ? getTopCompaniesFromPayV2(topNData.pay_v2)?.slice(0, TOP_N_LIMIT)
      : undefined;

  const compositionVariablesV21 = (() => {
    if (
      isRicsCompanyBreakdown &&
      topEntities != null &&
      topEntities.length > 0
    ) {
      const filtersWithoutRics = omit(mergedFiltersV21, [
        'rics_k10',
        'rics_k50',
        'rics_k400',
      ]);
      const entities = topEntities;
      return {
        ...drilldownVariablesV21,
        filters: stripEntitiesFromFilters(filtersWithoutRics, entities),
        entities,
      };
    }
    return drilldownVariablesV21;
  })();

  const compositionPause =
    isRicsCompanyBreakdown && (!topEntities || topEntities.length === 0);

  const [{ data: dataV21, status: compositionStatusV21 }] =
    useUrqlQueryWithStatus<CompositionV3DataQuery>({
      query: COMPOSITION_GET_DATA_V3,
      variables: compositionVariablesV21,
      pause: compositionPause,
    });

  const status = (() => {
    if (parentLookupFetching) {
      return DataProviderQueryStatus.Fetching;
    }
    if (isRicsCompanyBreakdown) {
      return combineQueryStatus([topNStatus, compositionStatusV21]);
    }
    return compositionStatusV21;
  })();

  const monthRefs = getMonthRefsFromCompositionV3Filters(
    compositionVariablesV21.filters
  );

  const finalData = (() => {
    if (
      isRicsCompanyBreakdown &&
      (compositionPause ||
        (topNData != null && (!topEntities || topEntities.length === 0)))
    ) {
      return undefined;
    }
    const v21 = dataV21?.composition_v3;
    if (!v21) {
      return undefined;
    }
    return filterOutSkillsOtherBucket(v21, segment);
  })();

  return (
    <>
      {children({
        data: finalData,
        monthRefs,
        status,
        parentLookup,
        parentTotals,
        isNested,
        displayLevelId,
        isDisplayLeaf,
        parentLevelId,
      })}
    </>
  );
}
